import { DEFAULT_RULE_SETS } from '../data/rulesEngineDefaults'
import type {
  BenefitRule,
  RuleAction,
  RuleOperator,
  RuleSetRecord,
  RuleSetStatus,
  UpdateRuleInput,
} from '../types/rulesEngine'
import { apiJsonRequest } from './apiClient'

const STORAGE_KEY = 'healthlynk_rules_engine_v1'
const RULE_SETS_PATH = '/rule-sets'
export const RULES_ENGINE_UPDATED_EVENT = 'healthlynk:rules-engine-updated'

export type RuleSetInput = {
  name: string
  productServiceSlug?: string
  rules?: BenefitRule[]
  isActive?: boolean
  status?: RuleSetStatus
}

type ApiMasterRef = {
  id?: string
  value_key?: string
  valueKey?: string
  display_label?: string
  displayLabel?: string
  key?: string
}

type ApiRuleRow = {
  id?: string
  name?: string
  condition_field_master_id?: string
  condition_operator_master_id?: string
  condition_value_master_id?: string | null
  condition_value?: string
  on_pass_action_master_id?: string
  on_fail_action_master_id?: string
  condition_field?: ApiMasterRef | string
  condition_operator?: ApiMasterRef | string
  condition_value_master?: ApiMasterRef | string
  on_pass_action?: ApiMasterRef | string
  on_fail_action?: ApiMasterRef | string
  field?: string
  operator?: string
  value?: string
  action?: string
  fail_action?: string
  failAction?: string
  fail_message?: string
  failMessage?: string
}

type ApiRuleSetRow = {
  id?: string
  name?: string
  product_service_slug?: string
  productServiceSlug?: string
  rules?: ApiRuleRow[]
  rules_count?: number
  rulesCount?: number
  status?: string
  is_active?: boolean
  isActive?: boolean
  created_at?: string
  createdAt?: string
  updated_at?: string
  updatedAt?: string
}

type ApiListResponse =
  | ApiRuleSetRow[]
  | {
      data?: ApiRuleSetRow[] | { items?: ApiRuleSetRow[]; results?: ApiRuleSetRow[] }
      items?: ApiRuleSetRow[]
      results?: ApiRuleSetRow[]
    }

type ApiSingleResponse = ApiRuleSetRow | { data?: ApiRuleSetRow; item?: ApiRuleSetRow }

type ApiRuleResponse = ApiRuleRow | { data?: ApiRuleRow; item?: ApiRuleRow }

const RULE_OPERATORS = new Set<RuleOperator>(['equals', 'not_equals', 'gt', 'gte', 'lt', 'lte'])
const RULE_ACTIONS = new Set<RuleAction>([
  'continue',
  'approve',
  'deny',
  'route_prior_auth',
  'flag',
])

function notifyUpdated() {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(RULES_ENGINE_UPDATED_EVENT))
}

function loadFromStorage(): RuleSetRecord[] | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as RuleSetRecord[]
    return Array.isArray(parsed) ? parsed : null
  } catch {
    return null
  }
}

function saveToStorage(rows: RuleSetRecord[]) {
  if (typeof window === 'undefined') return
  localStorage.setItem(STORAGE_KEY, JSON.stringify(rows))
  notifyUpdated()
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null
}

function readString(...candidates: unknown[]): string {
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) return candidate.trim()
  }
  return ''
}

function readNumber(...candidates: unknown[]): number | undefined {
  for (const candidate of candidates) {
    if (typeof candidate === 'number' && Number.isFinite(candidate)) return candidate
    if (typeof candidate === 'string' && candidate.trim() && !Number.isNaN(Number(candidate))) {
      return Number(candidate)
    }
  }
  return undefined
}

export function getRuleSetRulesCount(ruleSet: RuleSetRecord): number {
  if (typeof ruleSet.rulesCount === 'number') return ruleSet.rulesCount
  return ruleSet.rules.length
}

function masterValueKey(ref: ApiMasterRef | string | undefined): string {
  if (!ref) return ''
  if (typeof ref === 'string') return ref.trim()
  return readString(ref.value_key, ref.valueKey, ref.key, ref.display_label, ref.displayLabel)
}

function masterId(ref: ApiMasterRef | string | undefined, fallbackId?: string): string {
  if (fallbackId?.trim()) return fallbackId.trim()
  if (!ref) return ''
  if (typeof ref === 'string') return ref.trim()
  return readString(ref.id)
}

function asOperator(value: string): RuleOperator {
  return RULE_OPERATORS.has(value as RuleOperator) ? (value as RuleOperator) : 'equals'
}

function asAction(value: string): RuleAction {
  return RULE_ACTIONS.has(value as RuleAction) ? (value as RuleAction) : 'continue'
}

function statusToActive(status: string | undefined, isActive?: boolean): boolean {
  if (typeof isActive === 'boolean') return isActive
  if (!status) return true
  return status.trim().toLowerCase() !== 'inactive'
}

function activeToStatus(isActive: boolean | undefined, status?: RuleSetStatus): RuleSetStatus {
  if (status) return status
  return isActive === false ? 'inactive' : 'active'
}

function unwrapRuleSet(body: ApiSingleResponse): ApiRuleSetRow {
  const record = asRecord(body)
  if (!record) return {}
  const data = asRecord(record.data)
  if (data) return data as ApiRuleSetRow
  const item = asRecord(record.item)
  if (item) return item as ApiRuleSetRow
  return record as ApiRuleSetRow
}

function unwrapRule(body: ApiRuleResponse): ApiRuleRow {
  const record = asRecord(body)
  if (!record) return {}
  const data = asRecord(record.data)
  if (data) return data as ApiRuleRow
  const item = asRecord(record.item)
  if (item) return item as ApiRuleRow
  return record as ApiRuleRow
}

function asRuleSetArray(body: ApiListResponse): ApiRuleSetRow[] {
  if (Array.isArray(body)) return body
  if (!body || typeof body !== 'object') return []

  if (Array.isArray(body.items)) return body.items
  if (Array.isArray(body.results)) return body.results

  const data = body.data
  if (Array.isArray(data)) return data
  if (data && typeof data === 'object') {
    if (Array.isArray(data.items)) return data.items
    if (Array.isArray(data.results)) return data.results
  }
  return []
}

function normalizeRule(row: ApiRuleRow, index: number): BenefitRule {
  const id = readString(row.id) || `rule-${index + 1}`
  const name = readString(row.name) || `Rule ${index + 1}`

  const field =
    masterValueKey(row.condition_field) ||
    readString(row.field) ||
    'networkStatus'
  const operator = asOperator(
    masterValueKey(row.condition_operator) || readString(row.operator) || 'equals'
  )
  const value = readString(row.condition_value, row.value) || ''
  const action = asAction(
    masterValueKey(row.on_pass_action) || readString(row.action) || 'continue'
  )
  const failAction = asAction(
    masterValueKey(row.on_fail_action) ||
      readString(row.fail_action, row.failAction) ||
      'flag'
  )

  return {
    id,
    name,
    field,
    operator,
    value,
    action,
    failAction,
    failMessage: readString(row.fail_message, row.failMessage) || 'Rule failed',
    conditionFieldMasterId:
      masterId(row.condition_field, row.condition_field_master_id) || undefined,
    conditionOperatorMasterId:
      masterId(row.condition_operator, row.condition_operator_master_id) || undefined,
    conditionValueMasterId:
      masterId(row.condition_value_master, row.condition_value_master_id ?? undefined) ||
      undefined,
    onPassActionMasterId:
      masterId(row.on_pass_action, row.on_pass_action_master_id) || undefined,
    onFailActionMasterId:
      masterId(row.on_fail_action, row.on_fail_action_master_id) || undefined,
  }
}

function normalizeRules(rules: ApiRuleRow[] | BenefitRule[] | undefined): BenefitRule[] {
  if (!Array.isArray(rules)) return []
  return rules.map((row, index) => {
    if (row && typeof row === 'object' && 'field' in row && 'operator' in row && 'action' in row) {
      const local = row as BenefitRule
      return {
        ...local,
        id: local.id || `rule-${index + 1}`,
      }
    }
    return normalizeRule(row as ApiRuleRow, index)
  })
}

function ruleToCreatePayload(rule: BenefitRule) {
  return {
    name: rule.name.trim() || 'New Rule',
    ...(rule.conditionFieldMasterId
      ? { condition_field_master_id: rule.conditionFieldMasterId }
      : {}),
    ...(rule.conditionOperatorMasterId
      ? { condition_operator_master_id: rule.conditionOperatorMasterId }
      : {}),
    ...(rule.conditionValueMasterId
      ? { condition_value_master_id: rule.conditionValueMasterId }
      : {}),
    condition_value: rule.value,
    ...(rule.onPassActionMasterId ? { on_pass_action_master_id: rule.onPassActionMasterId } : {}),
    ...(rule.onFailActionMasterId ? { on_fail_action_master_id: rule.onFailActionMasterId } : {}),
  }
}

function ruleToUpdatePayload(rule: BenefitRule): UpdateRuleInput {
  return {
    name: rule.name.trim() || 'New Rule',
    condition_value: rule.value,
    ...(rule.conditionFieldMasterId
      ? { condition_field_master_id: rule.conditionFieldMasterId }
      : {}),
    ...(rule.conditionOperatorMasterId
      ? { condition_operator_master_id: rule.conditionOperatorMasterId }
      : {}),
    condition_value_master_id: rule.conditionValueMasterId ?? null,
    ...(rule.onPassActionMasterId ? { on_pass_action_master_id: rule.onPassActionMasterId } : {}),
    ...(rule.onFailActionMasterId ? { on_fail_action_master_id: rule.onFailActionMasterId } : {}),
  }
}

/** Client-only ids from Add Rule (`r1735…`, `local-…`, fallback `rule-1`). */
export function isUnsavedRuleId(id: string | undefined): boolean {
  if (!id?.trim()) return true
  return /^(r\d+|rule-\d+|local-)/i.test(id.trim())
}

function appendLocalRule(setId: string, rule: BenefitRule): BenefitRule | null {
  const store = getLocalStore()
  if (!store.some((row) => row.id === setId)) return null
  const persisted: BenefitRule = {
    ...rule,
    id: rule.id?.trim() && !isUnsavedRuleId(rule.id) ? rule.id : `local-${Date.now()}`,
    name: rule.name.trim() || 'New Rule',
  }
  saveToStorage(
    store.map((row) => {
      if (row.id !== setId) return row
      return {
        ...row,
        rules: [...row.rules, persisted],
        rulesCount: (row.rulesCount ?? row.rules.length) + 1,
        updatedAt: new Date().toISOString(),
      }
    })
  )
  return persisted
}

function normalizeRuleSet(row: ApiRuleSetRow): RuleSetRecord | null {
  const id = readString(row.id)
  const name = readString(row.name)
  if (!id || !name) return null
  const now = new Date().toISOString()
  const isActive = statusToActive(row.status, row.isActive ?? row.is_active)
  const rules = normalizeRules(row.rules)
  const rulesCount = readNumber(row.rules_count, row.rulesCount) ?? rules.length
  return {
    id,
    name,
    productServiceSlug:
      readString(row.productServiceSlug, row.product_service_slug) || undefined,
    rules,
    rulesCount,
    isActive,
    status: isActive ? 'active' : 'inactive',
    createdAt: readString(row.createdAt, row.created_at) || now,
    updatedAt: readString(row.updatedAt, row.updated_at) || now,
  }
}

function getLocalStore(): RuleSetRecord[] {
  const stored = loadFromStorage()
  const rows = stored ?? JSON.parse(JSON.stringify(DEFAULT_RULE_SETS))
  return rows.map((row: RuleSetRecord) => ({
    ...row,
    rules: Array.isArray(row.rules) ? row.rules : [],
    rulesCount: row.rulesCount ?? (Array.isArray(row.rules) ? row.rules.length : 0),
    status: row.status ?? (row.isActive === false ? 'inactive' : 'active'),
  }))
}

function filterLocal(rows: RuleSetRecord[], q?: string): RuleSetRecord[] {
  const needle = q?.trim().toLowerCase()
  const filtered = needle
    ? rows.filter(
        (r) =>
          r.name.toLowerCase().includes(needle) ||
          (r.productServiceSlug ?? '').toLowerCase().includes(needle)
      )
    : rows
  return [...filtered].sort((a, b) => a.name.localeCompare(b.name))
}

/** GET /rule-sets?q= */
export async function listRuleSets(q?: string): Promise<RuleSetRecord[]> {
  try {
    const params = new URLSearchParams()
    if (q?.trim()) params.set('q', q.trim())
    const query = params.toString()
    const path = query ? `${RULE_SETS_PATH}?${query}` : RULE_SETS_PATH
    const response = await apiJsonRequest<ApiListResponse>(path, { method: 'GET' })
    const rows = asRuleSetArray(response)
      .map(normalizeRuleSet)
      .filter(Boolean) as RuleSetRecord[]
    return filterLocal(rows)
  } catch {
    return filterLocal(getLocalStore(), q)
  }
}

/** GET /rule-sets/:ruleSetId */
export async function getRuleSet(id: string): Promise<RuleSetRecord | null> {
  const ruleSetId = id.trim()
  if (!ruleSetId) return null

  try {
    const response = await apiJsonRequest<ApiSingleResponse>(
      `${RULE_SETS_PATH}/${encodeURIComponent(ruleSetId)}`,
      { method: 'GET' }
    )
    const normalized = normalizeRuleSet(unwrapRuleSet(response))
    if (normalized) return normalized
  } catch {
    // fall through to local
  }

  return getLocalStore().find((r) => r.id === ruleSetId) ?? null
}

/** POST /rule-sets */
export async function createRuleSet(input: RuleSetInput): Promise<RuleSetRecord> {
  const name = input.name.trim()
  if (!name) throw new Error('Rule set name is required.')

  const status = activeToStatus(input.isActive, input.status)
  const rules = input.rules ?? []

  try {
    const response = await apiJsonRequest<ApiSingleResponse>(RULE_SETS_PATH, {
      method: 'POST',
      body: JSON.stringify({
        name,
        product_service_slug: input.productServiceSlug?.trim() || undefined,
        status,
        rules: rules.map(ruleToCreatePayload),
      }),
    })
    const normalized = normalizeRuleSet(unwrapRuleSet(response))
    if (normalized) {
      notifyUpdated()
      return normalized
    }
  } catch {
    // fall through to local storage
  }

  const now = new Date().toISOString()
  const record: RuleSetRecord = {
    id: `rs-${Date.now()}`,
    name,
    productServiceSlug: input.productServiceSlug?.trim() || undefined,
    rules,
    isActive: status === 'active',
    status,
    createdAt: now,
    updatedAt: now,
  }
  const next = [...getLocalStore(), record]
  saveToStorage(next)
  return record
}

function upsertLocalRuleSet(id: string, input: Partial<RuleSetInput>): RuleSetRecord {
  const store = getLocalStore()
  const existing = store.find((row) => row.id === id)
  const now = new Date().toISOString()

  if (!existing) {
    const status = activeToStatus(input.isActive, input.status)
    const record: RuleSetRecord = {
      id,
      name: (input.name ?? '').trim() || 'Rule set',
      productServiceSlug: input.productServiceSlug?.trim() || undefined,
      rules: input.rules ?? [],
      isActive: status === 'active',
      status,
      createdAt: now,
      updatedAt: now,
    }
    saveToStorage([...store, record])
    return record
  }

  const status =
    input.status ??
    (input.isActive !== undefined ? activeToStatus(input.isActive) : existing.status)
  const updated: RuleSetRecord = {
    ...existing,
    ...(input.name !== undefined ? { name: input.name.trim() } : {}),
    ...(input.productServiceSlug !== undefined
      ? { productServiceSlug: input.productServiceSlug.trim() || undefined }
      : {}),
    ...(input.rules !== undefined ? { rules: input.rules } : {}),
    ...(status !== undefined
      ? { status, isActive: status === 'active' }
      : input.isActive !== undefined
        ? { isActive: input.isActive, status: activeToStatus(input.isActive) }
        : {}),
    updatedAt: now,
  }
  saveToStorage(store.map((row) => (row.id === id ? updated : row)))
  return updated
}

/** PUT /rule-sets/:ruleSetId */
export async function updateRuleSet(id: string, input: Partial<RuleSetInput>): Promise<RuleSetRecord> {
  const ruleSetId = id.trim()
  if (!ruleSetId) throw new Error('Rule set id is required.')

  const name = (input.name ?? '').trim()
  const status = activeToStatus(input.isActive, input.status)
  const productServiceSlug = input.productServiceSlug?.trim() || undefined

  try {
    const response = await apiJsonRequest<ApiSingleResponse>(
      `${RULE_SETS_PATH}/${encodeURIComponent(ruleSetId)}`,
      {
        method: 'PUT',
        body: JSON.stringify({
          name: name || 'Rule set',
          product_service_slug: productServiceSlug,
          status,
        }),
      }
    )
    const normalized = normalizeRuleSet(unwrapRuleSet(response))
    notifyUpdated()

    const store = getLocalStore()
    if (store.some((row) => row.id === ruleSetId)) {
      upsertLocalRuleSet(ruleSetId, {
        ...input,
        name: normalized?.name ?? (name || input.name),
        productServiceSlug: normalized?.productServiceSlug ?? productServiceSlug,
        status: normalized?.status ?? status,
        isActive: normalized?.isActive,
        rules: input.rules,
      })
    }

    if (normalized) {
      return {
        ...normalized,
        rules: normalized.rules.length > 0 ? normalized.rules : (input.rules ?? normalized.rules),
        rulesCount: normalized.rulesCount ?? input.rules?.length ?? normalized.rules.length,
      }
    }
  } catch {
    // fall through to local storage
  }

  return upsertLocalRuleSet(ruleSetId, input)
}

/** POST /rule-sets/:ruleSetId/rules */
export async function createRule(ruleSetId: string, rule: BenefitRule): Promise<BenefitRule> {
  const setId = ruleSetId.trim()
  if (!setId) throw new Error('Rule set id is required.')

  const payload = ruleToCreatePayload(rule)

  try {
    const response = await apiJsonRequest<ApiRuleResponse>(
      `${RULE_SETS_PATH}/${encodeURIComponent(setId)}/rules`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      }
    )
    const normalized = normalizeRule(unwrapRule(response), 0)
    const merged: BenefitRule = {
      ...rule,
      ...normalized,
      id: normalized.id || rule.id,
      name: payload.name || normalized.name || rule.name,
      value: payload.condition_value || normalized.value || rule.value,
      field: normalized.field || rule.field,
      operator: normalized.operator || rule.operator,
      action: normalized.action || rule.action,
      failAction: normalized.failAction || rule.failAction,
      failMessage: normalized.failMessage || rule.failMessage,
      conditionFieldMasterId: rule.conditionFieldMasterId ?? normalized.conditionFieldMasterId,
      conditionOperatorMasterId:
        rule.conditionOperatorMasterId ?? normalized.conditionOperatorMasterId,
      conditionValueMasterId: rule.conditionValueMasterId ?? normalized.conditionValueMasterId,
      onPassActionMasterId: rule.onPassActionMasterId ?? normalized.onPassActionMasterId,
      onFailActionMasterId: rule.onFailActionMasterId ?? normalized.onFailActionMasterId,
    }
    notifyUpdated()

    const store = getLocalStore()
    if (store.some((r) => r.id === setId)) {
      saveToStorage(
        store.map((row) => {
          if (row.id !== setId) return row
          const withoutDraft = row.rules.filter((existing) => existing.id !== rule.id)
          return {
            ...row,
            rules: [...withoutDraft, merged],
            rulesCount: withoutDraft.length + 1,
            updatedAt: new Date().toISOString(),
          }
        })
      )
    }

    return merged
  } catch (error) {
    const local = appendLocalRule(setId, rule)
    if (local) return local
    throw error instanceof Error ? error : new Error('Unable to add rule.')
  }
}

/** PUT /rule-sets/:ruleSetId/rules/:ruleId */
export async function updateRule(
  ruleSetId: string,
  ruleId: string,
  input: UpdateRuleInput | BenefitRule
): Promise<BenefitRule> {
  const setId = ruleSetId.trim()
  const id = ruleId.trim()
  if (!setId || !id) throw new Error('Rule set id and rule id are required.')

  const payload: UpdateRuleInput =
    'field' in input && 'operator' in input
      ? ruleToUpdatePayload(input as BenefitRule)
      : (input as UpdateRuleInput)

  try {
    const response = await apiJsonRequest<ApiRuleResponse>(
      `${RULE_SETS_PATH}/${encodeURIComponent(setId)}/rules/${encodeURIComponent(id)}`,
      {
        method: 'PUT',
        body: JSON.stringify(payload),
      }
    )
    const normalized = normalizeRule(unwrapRule(response), 0)
    const merged: BenefitRule = {
      ...normalized,
      id,
      name: payload.name ?? normalized.name,
      value: payload.condition_value ?? normalized.value,
      conditionFieldMasterId: payload.condition_field_master_id ?? normalized.conditionFieldMasterId,
      conditionOperatorMasterId:
        payload.condition_operator_master_id ?? normalized.conditionOperatorMasterId,
      conditionValueMasterId:
        payload.condition_value_master_id === null
          ? undefined
          : payload.condition_value_master_id ?? normalized.conditionValueMasterId,
      onPassActionMasterId: payload.on_pass_action_master_id ?? normalized.onPassActionMasterId,
      onFailActionMasterId: payload.on_fail_action_master_id ?? normalized.onFailActionMasterId,
    }
    notifyUpdated()

    const store = getLocalStore()
    if (store.some((r) => r.id === setId)) {
      saveToStorage(
        store.map((row) => {
          if (row.id !== setId) return row
          return {
            ...row,
            rules: row.rules.map((rule) => (rule.id === id ? { ...rule, ...merged } : rule)),
            updatedAt: new Date().toISOString(),
          }
        })
      )
    }

    return merged
  } catch (error) {
    const store = getLocalStore()
    let updatedRule: BenefitRule | null = null
    const next = store.map((row) => {
      if (row.id !== setId) return row
      const rules = row.rules.map((rule) => {
        if (rule.id !== id) return rule
        updatedRule = {
          ...rule,
          ...(payload.name !== undefined ? { name: payload.name } : {}),
          ...(payload.condition_value !== undefined ? { value: payload.condition_value } : {}),
          ...(payload.condition_field_master_id !== undefined
            ? { conditionFieldMasterId: payload.condition_field_master_id }
            : {}),
          ...(payload.condition_operator_master_id !== undefined
            ? { conditionOperatorMasterId: payload.condition_operator_master_id }
            : {}),
          ...(payload.condition_value_master_id !== undefined
            ? {
                conditionValueMasterId: payload.condition_value_master_id ?? undefined,
              }
            : {}),
          ...(payload.on_pass_action_master_id !== undefined
            ? { onPassActionMasterId: payload.on_pass_action_master_id }
            : {}),
          ...(payload.on_fail_action_master_id !== undefined
            ? { onFailActionMasterId: payload.on_fail_action_master_id }
            : {}),
        }
        return updatedRule
      })
      return { ...row, rules, updatedAt: new Date().toISOString() }
    })

    if (updatedRule) {
      saveToStorage(next)
      return updatedRule
    }

    throw error instanceof Error ? error : new Error('Unable to update rule.')
  }
}

/** DELETE /rule-sets/:ruleSetId/rules/:ruleId */
export async function deleteRule(ruleSetId: string, ruleId: string): Promise<void> {
  const setId = ruleSetId.trim()
  const id = ruleId.trim()
  if (!setId || !id) throw new Error('Rule set id and rule id are required.')

  const removeFromStore = () => {
    const store = getLocalStore()
    if (!store.some((row) => row.id === setId)) return false
    saveToStorage(
      store.map((row) => {
        if (row.id !== setId) return row
        const rules = row.rules.filter((rule) => rule.id !== id)
        return {
          ...row,
          rules,
          rulesCount: rules.length,
          updatedAt: new Date().toISOString(),
        }
      })
    )
    return true
  }

  try {
    await apiJsonRequest(
      `${RULE_SETS_PATH}/${encodeURIComponent(setId)}/rules/${encodeURIComponent(id)}`,
      { method: 'DELETE' }
    )
    notifyUpdated()
    removeFromStore()
    return
  } catch (error) {
    if (removeFromStore()) {
      notifyUpdated()
      return
    }
    throw error instanceof Error ? error : new Error('Unable to delete rule.')
  }
}

/** DELETE /rule-sets/:ruleSetId */
export async function deleteRuleSet(id: string): Promise<void> {
  try {
    await apiJsonRequest(`${RULE_SETS_PATH}/${encodeURIComponent(id)}`, { method: 'DELETE' })
    notifyUpdated()

    const store = getLocalStore()
    if (store.some((r) => r.id === id)) {
      saveToStorage(store.filter((r) => r.id !== id))
    }
    return
  } catch {
    // fall through
  }

  const store = getLocalStore()
  const next = store.filter((r) => r.id !== id)
  if (next.length === store.length) throw new Error('Rule set not found.')
  saveToStorage(next)
}

export function resetRuleSetsToDefaults() {
  saveToStorage(JSON.parse(JSON.stringify(DEFAULT_RULE_SETS)))
}
